import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config';
import './AltaSocio.css';
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';

function AltaSocio() {
  const navigate = useNavigate();
  // Obtener el usuario logueado desde localStorage
  const usuarioLogueado = JSON.parse(localStorage.getItem('usuario') || '{}');
  
  const [form, setForm] = useState({
    nombreCompleto: '',
    direccion: '',
    correo: '',
    documento: '',
    tipoDocumento: 'CI Uruguay', // Valor predefinido
    telefono: '',
    fechaNacimiento: '',
    anioUltimaCuota: new Date().getFullYear().toString(), // Año actual predefinido
    numeroSocio: 'Automático',
    fechaIngreso: new Date().toISOString().split('T')[0], // Fecha actual
    categoria: 'Colaborador',
    usuarioAlta: usuarioLogueado.nombreCompleto || 'Usuario actual',
    contrasena: 'Autogenerada',
    administrador: 'No'
  });
  const [codigoPais, setCodigoPais] = useState('+598'); // Uruguay por defecto
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [fotoKey, setFotoKey] = useState(Date.now());
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [emailForPassword, setEmailForPassword] = useState('');
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [passwordGenerated, setPasswordGenerated] = useState(false);
  const fileInputRef = useRef();

  // Función para obtener el siguiente número de socio
  const obtenerSiguienteNumeroSocio = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.SOCIOS}/next-number`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setForm(prevForm => ({
          ...prevForm,
          numeroSocio: data.nextNumber.toString()
        }));
      } else {
        setForm(prevForm => ({
          ...prevForm,
          numeroSocio: '1'
        }));
      }
    } catch (error) {
      setForm(prevForm => ({
        ...prevForm,
        numeroSocio: '1'
      }));
    }
  };

  // useEffect para obtener el siguiente número de socio al cargar el componente
  useEffect(() => {
    obtenerSiguienteNumeroSocio();
  }, []);

  // Función para verificar si el documento ya existe
  const verificarDocumentoExistente = async (documento) => {
    if (!documento || documento.trim() === '') return false;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.SOCIOS}?documento=${encodeURIComponent(documento.trim())}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.socios && data.socios.length > 0;
      }
      return false;
    } catch (error) {
      console.error('Error verificando documento:', error);
      return false;
    }
  };

  // Función para generar contraseña y enviarla por correo
  const handleGeneratePassword = async () => {
    if (!emailForPassword.trim()) {
      setError('Por favor ingrese un correo electrónico válido');
      return;
    }
    
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailForPassword)) {
      setError('Por favor ingrese un correo electrónico válido');
      return;
    }

    setGeneratingPassword(true);
    setError('');
    setMensaje('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.SOCIOS}/generate-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: emailForPassword,
          nombreCompleto: form.nombreCompleto,
          documento: form.documento
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMensaje('Contraseña generada y enviada por correo electrónico');
        setPasswordGenerated(true);
        setForm(prevForm => ({
          ...prevForm,
          contrasena: 'Generada y enviada por correo'
        }));
        setShowPasswordModal(false);
        setEmailForPassword('');
      } else {
        setError(data.message || 'Error al generar la contraseña. Verifique la configuración del servidor de correo.');
      }
    } catch (error) {
      setError('Error de red al generar la contraseña');
    }

    setGeneratingPassword(false);
  };

  // Campos con valores predefinidos
  const camposPredefinidos = {
    direccion: '',
    tipoDocumento: 'CI Uruguay',
    anioUltimaCuota: new Date().getFullYear().toString(),
    codigoPais: '+598',
    numeroSocio: 'Automático (siguiente disponible)',
    fechaIngreso: new Date().toLocaleDateString('es-ES'),
    categoria: 'Colaborador',
    usuarioAlta: usuarioLogueado.nombreCompleto || 'Usuario actual del sistema',
    contrasena: 'Autogenerada por el sistema',
    administrador: 'No'
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCodigoPaisChange = e => {
    setCodigoPais(e.target.value);
  };

  const handleTelefonoChange = value => {
    setForm({ ...form, telefono: value });
  };

  const handleFotoChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFoto(file);
      setSubiendoFoto(true);
      
      try {
        // Mostrar preview inmediatamente
        const previewUrl = URL.createObjectURL(file);
        setFotoPreview(previewUrl);
        setFotoKey(Date.now());
        setMensaje('Foto seleccionada correctamente');
      } catch (err) {
        setError('Error al procesar la imagen');
      }
      
      setSubiendoFoto(false);
    }
  };

  // Función para validar CI Uruguay
  function validarCiUruguay(ci) {
    // Eliminar puntos y guiones
    ci = ci.replace(/[.\-]/g, '');
    if (!/^\d{7,8}$/.test(ci)) return false;
    let a = 0;
    let ciArr = ci.split('').map(Number);
    let base = [2,9,8,7,6,3,4];
    while (ciArr.length < 8) ciArr.unshift(0);
    for (let i = 0; i < 7; i++) a += (base[i] * ciArr[i]) % 10;
    let check = (10 - (a % 10)) % 10;
    return check === ciArr[7];
  }

  const validar = () => {
    if (!form.nombreCompleto.trim()) return 'El nombre completo es obligatorio.';
    if (!form.documento?.trim()) return 'El documento es obligatorio.';
    if (!form.tipoDocumento) return 'El tipo de documento es obligatorio.';
    if (form.tipoDocumento === 'CI Uruguay' && !validarCiUruguay(form.documento)) return 'La cédula de identidad no es válida.';
    if (form.correo && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.correo)) return 'El correo no es válido.';
    if (form.anioUltimaCuota && (isNaN(form.anioUltimaCuota) || form.anioUltimaCuota < 1900)) return 'El año de la última cuota no es válido.';
    
    // Validar número de socio
    if (form.numeroSocio && form.numeroSocio !== 'Automático') {
      const numeroSocio = parseInt(form.numeroSocio);
      if (isNaN(numeroSocio) || numeroSocio <= 0) {
        return 'El número de socio debe ser un número válido mayor a 0.';
      }
    }
    
    // Validar formato del documento
    if (form.documento && form.documento.trim()) {
      const documento = form.documento.trim();
      if (documento.length < 3) {
        return 'El documento debe tener al menos 3 caracteres.';
      }
    }
    
    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setMensaje(''); setError(''); setContrasena('');
    const errorValidacion = validar();
    if (errorValidacion) return setError(errorValidacion);
    
    // Verificar si el documento ya existe
    if (form.documento && form.documento.trim()) {
      const documentoExiste = await verificarDocumentoExistente(form.documento);
      if (documentoExiste) {
        setError(`El documento ${form.documento} ya está registrado en la base de datos. Por favor, verifique el documento.`);
        return;
      }
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Agregar todos los campos del formulario
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'telefono' && value) {
          formData.append(key, codigoPais + ' ' + value);
        } else {
          formData.append(key, value);
        }
      });
      
      // Agregar la foto si existe
      if (foto) {
        formData.append('foto', foto);
      }

      const res = await fetch(API_ENDPOINTS.SOCIOS, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setMensaje('Socio dado de alta correctamente');
        setContrasena(data.contrasena);
        // Limpiar el formulario
        setForm({ 
          nombreCompleto: '', 
          direccion: '', // Campo vacío
          correo: '', 
          documento: '', 
          tipoDocumento: 'CI Uruguay', // Mantener valor predefinido
          telefono: '', 
          fechaNacimiento: '', 
          anioUltimaCuota: new Date().getFullYear().toString(), // Mantener valor predefinido
          numeroSocio: 'Automático',
          fechaIngreso: new Date().toISOString().split('T')[0],
          categoria: 'Colaborador',
          usuarioAlta: usuarioLogueado.nombreCompleto || 'Usuario actual',
          contrasena: 'Autogenerada',
          administrador: 'No'
        });
        setFoto(null);
        setFotoPreview('');
        setCodigoPais('+598');
        setPasswordGenerated(false);
      } else {
        setError(data.message || 'Error al dar de alta');
      }
    } catch (err) {
      setError('Error de red');
    }
    setLoading(false);
  };

  return (
    <div className="panel-socio-bg">
      <div className="panel-socio-card">
        <h2 className="panel-socio-title">➕ Alta de Socio</h2>
        
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '1rem', width: '100%', justifyContent: 'center'}}>
          {fotoPreview && <img key={fotoKey} src={fotoPreview} alt="Foto del socio" className="panel-socio-foto" />}
          <button
            type="button"
            className="btn-panel-socio btn-cambiar-foto"
            style={{marginLeft: 16, padding: '0.4rem 1rem', fontSize: '0.95rem', minWidth: 0, width: 'auto', height: 38}}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={subiendoFoto}
          >
            {subiendoFoto ? 'Procesando...' : 'Seleccionar foto'}
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{display: 'none'}}
            onChange={handleFotoChange}
          />
        </div>
        
        <form onSubmit={handleSubmit} className="panel-socio-form">
          <div className="form-columns">
            <div className="form-column">
              <div className="form-field-group">
                <label>Nombre Completo *</label>
                <input name="nombreCompleto" value={form.nombreCompleto} onChange={handleChange} required />
              </div>
              
              <div className="form-field-group">
                <label>Documento *</label>
                <input name="documento" value={form.documento} onChange={handleChange} required />
              </div>
              
              <div className="form-field-group">
                <label>Tipo de Documento *</label>
                <select name="tipoDocumento" value={form.tipoDocumento} onChange={handleChange} required>
                  <option value="">Seleccionar</option>
                  <option value="CI Uruguay">CI Uruguay</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              
              <div className="form-field-group">
                <label>Año de la última cuota paga</label>
                <input name="anioUltimaCuota" type="number" value={form.anioUltimaCuota} onChange={handleChange} />
              </div>
              

              
              <div className="form-field-group">
                <label>Fecha de Ingreso</label>
                <input name="fechaIngreso" type="date" value={form.fechaIngreso} onChange={handleChange} />
              </div>
              
              <div className="form-field-group">
                <label>Categoría</label>
                <select name="categoria" value={form.categoria} onChange={handleChange}>
                  <option value="Activo">Activo</option>
                  <option value="Colaborador">Colaborador</option>
                  <option value="Suscriptor">Suscriptor</option>
                  <option value="Dama">Dama</option>
                  <option value="Menor">Menor</option>
                  <option value="Fallecido">Fallecido</option>
                  <option value="Borrado">Borrado</option>
                </select>
              </div>
              
              <div className="form-field-group">
                <label>Administrador</label>
                <select name="administrador" value={form.administrador} onChange={handleChange}>
                  <option value="No">No</option>
                  <option value="Sí">Sí</option>
                </select>
              </div>
            </div>
            
            <div className="form-column">
              <div className="form-field-group">
                <label>Dirección</label>
                <input name="direccion" value={form.direccion} onChange={handleChange} />
              </div>
              
              <div className="form-field-group">
                <label>Correo electrónico</label>
                <input name="correo" type="email" value={form.correo} onChange={handleChange} />
              </div>
              
              <div className="form-field-group">
                <label>Número de Socio</label>
                <input name="numeroSocio" value={form.numeroSocio} onChange={handleChange} />
              </div>
              
              <div className="form-field-group">
                <label>Teléfono</label>
                <PhoneInput
                  country={'uy'}
                  value={form.telefono}
                  onChange={handleTelefonoChange}
                  preferredCountries={['uy', 'ar', 'br', 'cl', 'es']}
                  inputProps={{
                    name: 'telefono',
                    required: true,
                    autoFocus: false
                  }}
                />
              </div>
              
              <div className="form-field-group">
                <label>Fecha de nacimiento</label>
                <input name="fechaNacimiento" type="date" value={form.fechaNacimiento} onChange={handleChange} />
              </div>
              
              <div className="form-field-group">
                <label>Usuario que da de alta</label>
                <input name="usuarioAlta" value={form.usuarioAlta} onChange={handleChange} disabled style={{backgroundColor: '#f5f5f5', color: '#666'}} />
              </div>
              
              <div className="form-field-group">
                <label>Contraseña</label>
                <button
                  type="button"
                  className="btn-panel-socio btn-accion"
                  onClick={() => setShowPasswordModal(true)}
                  disabled={passwordGenerated}
                  style={{
                    width: '100%',
                    height: '38px',
                    fontSize: '0.9rem',
                    backgroundColor: passwordGenerated ? '#4CAF50' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: passwordGenerated ? 'default' : 'pointer'
                  }}
                >
                  {passwordGenerated ? '✅ Contraseña Generada' : '🔑 Generar Contraseña'}
                </button>
              </div>
            </div>
          </div>
          
          {mensaje && <div className="panel-socio-mensaje">{mensaje}</div>}
          {error && <div className="panel-socio-error" style={{
            background: '#d32f2f',
            color: '#ffffff',
            padding: '0.5rem',
            borderRadius: '6px',
            marginBottom: '0.5rem',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            border: '1px solid #b71c1c',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            letterSpacing: '0.3px'
          }}>{error}</div>}
          {contrasena && contrasena !== 'Enviada por correo' && <div className="panel-socio-mensaje" style={{background: 'linear-gradient(90deg, #4CAF50 0%, #45a049 100%)', color: 'white'}}>
            Contraseña generada: <b>{contrasena}</b>
          </div>}
          {contrasena === 'Enviada por correo' && <div className="panel-socio-mensaje" style={{background: 'linear-gradient(90deg, #2196F3 0%, #1976D2 100%)', color: 'white'}}>
            ✅ Contraseña enviada por correo electrónico
          </div>}
        </form>
        
        <div className="panel-socio-botones">
          <button type="button" className="btn-panel-socio btn-accion" onClick={handleSubmit} disabled={loading} style={{marginBottom: 8}}>
            {loading ? 'Procesando...' : '➕ Dar de alta'}
          </button>
        </div>
        
        <button className="btn-panel-socio btn-accion" onClick={() => navigate('/admin')} style={{marginTop: 16, marginBottom: 8}}>
          ⬅️ Volver al Panel
        </button>
      </div>

      {/* Modal para generar contraseña */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{marginTop: 0, marginBottom: '1rem', color: '#333'}}>
              🔑 Generar Contraseña
            </h3>
            <p style={{marginBottom: '1rem', color: '#666', fontSize: '0.9rem'}}>
              Ingrese el correo electrónico donde se enviará la contraseña generada:
            </p>
            
            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333'}}>
                Correo Electrónico *
              </label>
              <input
                type="email"
                value={emailForPassword}
                onChange={(e) => setEmailForPassword(e.target.value)}
                placeholder="ejemplo@correo.com"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                required
              />
            </div>

            <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setEmailForPassword('');
                  setError('');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={generatingPassword}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleGeneratePassword}
                disabled={generatingPassword || !emailForPassword.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: generatingPassword ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: generatingPassword ? 'not-allowed' : 'pointer'
                }}
              >
                {generatingPassword ? 'Generando...' : 'Generar y Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AltaSocio; 